import { Component } from '@theme/component';

/**
 * Tab switcher for the FAQ section.
 *
 * Panels are all present in the DOM so the questions stay crawlable; only visibility is toggled.
 *
 * @extends {Component<{ tab: HTMLButtonElement[], panel: HTMLElement[] }>}
 */
class FaqTabs extends Component {
  requiredRefs = ['tab', 'panel'];

  connectedCallback() {
    super.connectedCallback();
    this.#select(this.#activeIndex);
  }

  get #tabs() {
    return this.refs.tab ?? [];
  }

  get #panels() {
    return this.refs.panel ?? [];
  }

  get #activeIndex() {
    const index = this.#tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    return index === -1 ? 0 : index;
  }

  /**
   * Activates the tab that was clicked.
   *
   * `on:click` lives on the button itself: the event delegation rewrites `event.target` to the
   * element carrying the attribute, so a handler bound to the tablist could not tell them apart.
   *
   * @param {Event} event
   */
  handleTabClick(event) {
    const tab = event.target;
    if (!(tab instanceof HTMLElement)) return;

    const index = this.#tabs.indexOf(/** @type {HTMLButtonElement} */ (tab));
    if (index === -1) return;

    this.#select(index);
  }

  /**
   * Moves focus between tabs with the arrow, home and end keys.
   * @param {KeyboardEvent} event
   */
  handleTabKeydown(event) {
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!keys.includes(event.key)) return;

    event.preventDefault();

    const count = this.#tabs.length;
    const current = this.#activeIndex;
    let next = current;

    if (event.key === 'ArrowLeft') next = (current - 1 + count) % count;
    if (event.key === 'ArrowRight') next = (current + 1) % count;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = count - 1;

    this.#select(next);
    this.#tabs[next]?.focus();
  }

  /**
   * Shows the panel at the given index and hides the rest.
   * @param {number} index
   */
  #select(index) {
    this.#tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });

    this.#panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });
  }
}

if (!customElements.get('faq-tabs-component')) {
  customElements.define('faq-tabs-component', FaqTabs);
}
